import { useEffect, useRef } from "react";
import Cookies from "js-cookie";
import { decodeToken } from "../utils/authSession";
import { toPositiveId } from "../utils/ids";
import { WatchTimeCounter } from "../utils/watchTimeCounter";
import { isWatchDebugEnabled, watchLog } from "../utils/watchLog";
import { newSessionId, saveEntry } from "../utils/watchTimeStore";
import {
	TAB_ID,
	getWatchSyncStatus,
	startWatchTimeSync,
	subscribeWatchSync,
	syncPending,
} from "../utils/watchTimeSync";

// how often the media clock is sampled, how often the Local Storage copy is refreshed, and
// how often an in-progress session is checkpointed to the backend
const TICK_MS = 1000;
const PERSIST_MS = 5000;
const CHECKPOINT_MS = 30000;

// Debug panel is on for local development (`npm start`) and off in production builds unless
// REACT_APP_WATCH_DEBUG=true is set at build time.
export { isWatchDebugEnabled };

const isMediaElement = (node) =>
	Boolean(node) &&
	typeof node.addEventListener === "function" &&
	typeof node.currentTime === "number";

// genuinely playing: not paused/ended/seeking and enough data buffered to keep going
const isActive = (el) =>
	!el.paused && !el.ended && !el.seeking && el.readyState >= 3;

// Tracks how long the student actually watches the video rendered by ReactPlayer. Returns a
// ref for <ReactPlayer ref=...> and a stable `tracker` the debug panel subscribes to (so the
// player itself never re-renders because of tracking). Every failure inside is swallowed:
// tracking must never be able to break playback.
export function useVideoWatchTracker({ courseId, lessonId, videoId }) {
	const playerRef = useRef(null);
	const trackerRef = useRef(null);
	if (trackerRef.current === null) {
		const subscribers = new Set();
		trackerRef.current = {
			snapshot: null,
			subscribe(fn) {
				subscribers.add(fn);
				if (this.snapshot) fn(this.snapshot);
				return () => subscribers.delete(fn);
			},
			publish(snapshot) {
				this.snapshot = snapshot;
				subscribers.forEach((fn) => fn(snapshot));
			},
		};
	}

	useEffect(() => {
		const tracker = trackerRef.current;
		const token = Cookies.get("authToken");
		const payload = decodeToken(token);
		const studentId = payload && Number.isInteger(payload.id) ? payload.id : null;
		const course = toPositiveId(courseId);
		const video = toPositiveId(videoId);
		// without a real student / course / video there is nothing meaningful to record
		if (studentId === null || course === null || video === null) return undefined;

		try {
			startWatchTimeSync();
		} catch (error) {
			// syncing is best effort
		}

		const session = {
			sessionId: newSessionId(),
			studentId,
			courseId: course,
			lessonId: toPositiveId(lessonId) || video,
			videoId: video,
			startedAt: new Date().toISOString(),
			endedAt: null,
			seq: 0,
			closed: false,
			ownerTab: TAB_ID,
		};
		const counter = new WatchTimeCounter();
		watchLog("session started", {
			sessionId: session.sessionId,
			studentId,
			courseId: course,
			videoId: video,
		});
		let el = null;
		let lastPersist = 0;
		let lastCheckpoint = Date.now();
		let dirty = false;
		// set by pagehide: the browser pausing the media while it tears the page down must not
		// re-save the entry as "open" afterwards
		let pageHidden = false;
		// what the Local Storage copy currently holds, so an unchanged state is not re-saved
		let saved = null;

		const publish = () => {
			tracker.publish({
				sessionId: session.sessionId,
				watchedSeconds: counter.watchedSeconds,
				wallSeconds: counter.wallSeconds,
				rate: el ? el.playbackRate : 1,
				maxRate: counter.maxRate,
				active: el ? isActive(el) : false,
				sync: getWatchSyncStatus(),
			});
		};

		const tickNow = () => {
			if (!el) return 0;
			const added = counter.tick({
				mediaTime: el.currentTime,
				wallNow: performance.now(),
				rate: el.playbackRate,
				active: isActive(el),
				seeking: el.seeking,
			});
			if (added > 0) {
				dirty = true;
				watchLog(
					`+${added.toFixed(2)}s counted | total ${counter.watchedSeconds.toFixed(2)}s media / ${counter.wallSeconds.toFixed(2)}s real | speed ${el.playbackRate}x`
				);
			}
			return added;
		};

		// writes the Local Storage copy; no-op until something was actually watched
		const persist = (closed = false) => {
			if (counter.watchedMs <= 0 && counter.wallMs <= 0) return false;
			const nextClosed = closed || pageHidden;
			const watchedMs = Math.round(counter.watchedMs);
			if (
				saved &&
				saved.watchedMs === watchedMs &&
				saved.closed === nextClosed
			) {
				return true; // nothing new: keep the same seq so it is not re-sent
			}
			session.seq += 1;
			session.closed = nextClosed;
			saved = { watchedMs, closed: nextClosed };
			session.endedAt = new Date().toISOString();
			watchLog(
				`saved to Local Storage seq=${session.seq} watched=${(watchedMs / 1000).toFixed(2)}s closed=${nextClosed}`
			);
			saveEntry({
				...session,
				watchedMs,
				wallMs: Math.round(counter.wallMs),
				lastPositionMs: counter.lastPositionMs,
				maxRate: counter.maxRate,
				updatedAt: Date.now(),
			});
			lastPersist = Date.now();
			dirty = false;
			return true;
		};

		// settle the clock, persist, and push to the backend (pause / ended / hide / close)
		const flush = ({ closed = false, keepalive = false } = {}) => {
			try {
				tickNow();
				if (persist(closed)) {
					syncPending({ force: true, keepalive });
					lastCheckpoint = Date.now();
				}
				publish();
			} catch (error) {
				// never let tracking affect the player
			}
		};

		const onSettle = (event) => {
			watchLog(`event: ${event && event.type}`);
			flush();
		};
		const onSeeking = () => {
			watchLog("event: seeking (next interval not counted)");
			counter.markSeek();
		};
		const onSeeked = () => {
			try {
				tickNow();
			} catch (error) {
				// ignore
			}
		};
		// src swap (signed URL renewal), error, abort: the media clock is not trustworthy
		const onBreak = (event) => {
			try {
				watchLog(`event: ${event && event.type} (source reload / error)`);
				flush();
				counter.markBreak();
			} catch (error) {
				// ignore
			}
		};
		const onRate = (event) => {
			try {
				if (event) {
					watchLog(
						`event: ${event.type}${event.type === "ratechange" ? " -> " + el.playbackRate + "x" : ""}${event.type === "waiting" || event.type === "stalled" ? " (buffering, not counted)" : ""}`
					);
				}
				tickNow();
				publish();
			} catch (error) {
				// ignore
			}
		};
		const bound = [
			["pause", onSettle],
			["ended", onSettle],
			["playing", onRate],
			["waiting", onRate],
			["stalled", onRate],
			["ratechange", onRate],
			["seeking", onSeeking],
			["seeked", onSeeked],
			["emptied", onBreak],
			["loadstart", onBreak],
			["abort", onBreak],
			["error", onBreak],
		];

		const detach = () => {
			if (!el) return;
			bound.forEach(([name, fn]) => el.removeEventListener(name, fn));
			el = null;
		};
		// the <video> element only exists after ReactPlayer has loaded; look for it every tick
		const attach = () => {
			let node = null;
			try {
				node =
					playerRef.current && playerRef.current.getInternalPlayer
						? playerRef.current.getInternalPlayer()
						: null;
			} catch (error) {
				node = null;
			}
			if (!isMediaElement(node) || node === el) return;
			detach();
			el = node;
			bound.forEach(([name, fn]) => el.addEventListener(name, fn));
			counter.markBreak();
		};

		const timer = setInterval(() => {
			try {
				attach();
				const added = tickNow();
				const now = Date.now();
				if (dirty && now - lastPersist >= PERSIST_MS) persist(false);
				// periodic checkpoint while genuinely playing
				if (added > 0 && now - lastCheckpoint >= CHECKPOINT_MS) {
					if (persist(false)) syncPending({ own: true });
					lastCheckpoint = now;
				}
				publish();
			} catch (error) {
				// never let tracking affect the player
			}
		}, TICK_MS);

		// page hide: write to Local Storage synchronously (the guarantee), then try to send
		const onPageHide = () => {
			flush({ closed: true, keepalive: true });
			pageHidden = true;
		};
		const onPageShow = (event) => {
			// restored from the back/forward cache: the session is alive again
			if (event.persisted) {
				pageHidden = false;
				session.closed = false;
			}
		};
		const onVisibility = () => {
			if (document.visibilityState === "hidden") flush({ keepalive: true });
		};
		window.addEventListener("pagehide", onPageHide);
		window.addEventListener("pageshow", onPageShow);
		window.addEventListener("beforeunload", onPageHide);
		document.addEventListener("visibilitychange", onVisibility);
		const unsubscribeSync = subscribeWatchSync(() => {
			try {
				publish();
			} catch (error) {
				// ignore
			}
		});
		publish();

		return () => {
			clearInterval(timer);
			window.removeEventListener("pagehide", onPageHide);
			window.removeEventListener("pageshow", onPageShow);
			window.removeEventListener("beforeunload", onPageHide);
			document.removeEventListener("visibilitychange", onVisibility);
			unsubscribeSync();
			// player closed / navigated away / video changed
			watchLog("player closed / unmounted -> final flush");
			flush({ closed: true });
			detach();
		};
	}, [courseId, lessonId, videoId]);

	return { playerRef, tracker: trackerRef.current };
}
